import {
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import PosterTile from "../../components/PosterTile";
import {
  getContentLabel,
  getContentOptions,
  getStatusOptions,
} from "../../utils/utils";
import { isUserOriginList } from "../../utils/listSchema";
import { useStatusSummary } from "../../hooks/useCustomQuery";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import UserChip from "../../components/UserChip";
import VisibilityIcon from "../../components/VisibilityIcon";
import { useScaleGestures } from "../../hooks/useScaleGestures";
import { buildListPath } from "../../utils/detailPaths";
import { useThemeContext } from "../../context/themeContext";

// Gentle "breathing" scale loop for the tap hint — slow with a pause between cycles,
// so it reads as a hint rather than a distraction.
const HINT_TRANSITION = {
  duration: 1.6,
  times: [0, 0.5, 1],
  repeat: Infinity,
  // repeatDelay: 1.6,
  ease: "easeInOut",
};

export default function CollectionCover({
  list,
  reverse = true,
  onClick,
  isMobile,
  demoCompletion = null,
  showAuthor = false,
  isOwn,
  hint = false,
}) {
  const theme = useTheme();
  const statusOptions = getStatusOptions();
  const scaleGestures = useScaleGestures();
  const { isTouchDevice } = useThemeContext();

  // item_count is the true total from the backend; items[] may be capped by
  // max_items (explore/homepage carousels). A capped payload reports a larger
  // true total than the items it actually returned.
  const itemCount = list.item_count ?? list.items.length;
  const isCapped =
    list.item_count != null && list.item_count > list.items.length;

  // Observe the full list in cache WITHOUT triggering a fetch. Populated as a
  // side effect when the user opens the collection dialog, at which point every
  // item's status is prefetched — letting a capped card go live thereafter.
  const { data: cachedList } = useQuery({
    queryKey: ["list", list.list_id],
    enabled: false,
  });

  // Live count source: the full set once cached; otherwise the items we hold.
  // For a capped payload not yet opened, skip the per-item cache (it only covers
  // the poster subset and would undercount) and trust the backend total instead.
  const liveContentIds = useMemo(() => {
    if (cachedList) return cachedList.items.map((item) => item.content_id);
    return isCapped ? [] : list.items.map((item) => item.content_id);
  }, [cachedList, isCapped, list.items]);

  const { finishedCount: liveFinishedCount, isLoading: liveLoading } =
    useStatusSummary(liveContentIds);

  // Use the live cache-derived count when we hold a complete item set (full
  // payload, or capped-but-opened); otherwise fall back to the backend's
  // full-list finished_count so a capped card is still accurate before opening.
  const useLive = !isCapped || !!cachedList;
  const effectiveFinishedCount = useLive
    ? liveFinishedCount
    : list.finished_count;
  const effectiveItemCount =
    isCapped && cachedList ? cachedList.items.length : itemCount;
  const isFinishedCountLoading = useLive && liveLoading;

  // null → no finished data to show (logged out: backend omits finished_count).
  const finishedPercentage =
    effectiveItemCount > 0 && effectiveFinishedCount != null
      ? Math.round((effectiveFinishedCount / effectiveItemCount) * 100)
      : null;

  return (
    <Box
      component={motion.div}
      {...scaleGestures}
      sx={{ width: "100%" }}
      animate={hint ? { scale: [1, 1.015, 1] } : undefined}
      transition={hint ? HINT_TRANSITION : undefined}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          bgcolor: "inherit",
          // Static hover-style tint to hint the card is tappable (mobile, no hover).
          ...(hint && { backgroundColor: theme.palette.action.hover }),
        }}
      >
        {/* A real link to the collection's URL, so crawlers can follow it and
            modified clicks open a new tab; a plain click opens the dialog
            in-app via onClick instead. On touch devices the native long-press
            link menu/preview is suppressed so it behaves like an app tile. */}
        <CardActionArea
          component={RouterLink}
          to={buildListPath(list)}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            onClick?.();
          }}
          onContextMenu={isTouchDevice ? (e) => e.preventDefault() : undefined}
          sx={{ p: 1, ...(isTouchDevice && { WebkitTouchCallout: "none" }) }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={6}
              order={{ xs: !reverse ? 2 : 1, sm: reverse ? 2 : 1 }}
              display="flex"
              flexDirection="column"
              gap={1}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  textWrap: "balance",
                  pt: 0.5,
                  display: "-webkit-box",
                  overflow: "hidden",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  textAlign: { xs: "center", sm: reverse ? "left" : "right" },
                }}
              >
                {list.title}
              </Typography>
              {list.description && (
                <Typography
                  variant="body2"
                  sx={{
                    textWrap: "balance",
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    textAlign: { xs: "center", sm: reverse ? "left" : "right" },
                  }}
                >
                  {list.description}
                </Typography>
              )}

              <Stack
                direction={"row"}
                spacing={1.5}
                alignItems="center"
                justifyContent={
                  isMobile ? "center" : reverse ? "flex-start" : "flex-end"
                }
                // justifyContent="center"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {getContentOptions()[list.content_type].icon}
                  <Typography variant="body2">
                    {itemCount}{" "}
                    {list.content_type === "mixed"
                      ? itemCount === 1
                        ? "item"
                        : "items"
                      : getContentLabel({
                          type: list.content_type,
                          lowercase: true,
                          plural: itemCount !== 1,
                        })}
                  </Typography>
                </Stack>

                {isOwn && isUserOriginList(list) && (
                  <VisibilityIcon
                    value={list.visibility}
                    noun="collection"
                    disablePopover
                  />
                )}

                {itemCount === 0 ? null : isFinishedCountLoading ? (
                  <CircularProgress size="20px" />
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    {finishedPercentage === 100
                      ? statusOptions["finished"].iconFilled
                      : statusOptions["finished"].icon}
                    <Typography variant="body2">
                      {" "}
                      {/* demoCompletion is the logged-out demo value; otherwise an
                          em-dash when we have no finished data (e.g. logged out). */}
                      {demoCompletion != null
                        ? demoCompletion
                        : finishedPercentage == null
                          ? "—"
                          : finishedPercentage}
                      % finished
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {showAuthor && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  justifyContent={
                    isMobile ? "center" : reverse ? "flex-start" : "flex-end"
                  }
                >
                  <Typography variant="body2" color="text.secondary">
                    Curated by{" "}
                  </Typography>
                  <UserChip
                    username={
                      list.origin === "canonical" ? "curate" : list.username
                    }
                    avatarUrl={list.avatar_url}
                    interactive={false}
                    color="text.secondary"
                  />
                </Stack>
              )}
            </Grid>

            <Grid item xs={12} sm={6} order={reverse ? 1 : 2}>
              {itemCount > 0 ? (
                <Stack
                  spacing={"-10%"}
                  sx={{
                    justifyContent: { xs: "center", sm: "flex-start" },
                    "& > *": {
                      flexBasis: `calc(150% / 5)`,
                      maxWidth: `calc(150% / 5)`,
                    },
                  }}
                  direction={{
                    xs: "row-reverse",
                    sm: reverse ? "row-reverse" : "row",
                  }}
                >
                  {list.items.slice(0, 5).map((item, index) => (
                    <PosterTile
                      contentId={item.content_id}
                      contentType={item.content_type}
                      contentTitle={item.title}
                      imageUrl={item.image_url}
                      key={item.content_id}
                      padding={{ xs: 0.5, sm: 0.75 }}
                      zIndex={100 - index}
                    />
                  ))}
                </Stack>
              ) : (
                <Stack
                  direction={{
                    xs: "row-reverse",
                    sm: reverse ? "row-reverse" : "row",
                  }}
                  spacing={"-10%"}
                >
                  {Array(5)
                    .fill()
                    .map((_, index) => (
                      <PosterTile
                        key={index}
                        skeleton={true}
                        padding={0.75}
                        zIndex={100 - index}
                      />
                    ))}
                </Stack>
              )}
            </Grid>
          </Grid>
        </CardActionArea>
      </Card>
    </Box>
  );
}
